/**
 * @file care-item.schema.ts
 * @description Definição de um item de cuidado — checkpoint (fluxo de perguntas
 * em wizard) ou lembrete (card/notificação ligado a data).
 *
 * COLEÇÃO ÚNICA POLIMÓRFICA (`careItems`, discriminada por `type`), pelo mesmo
 * motivo da coleção `content`: o catálogo unificado do admin sai de graça, e o
 * vocabulário compartilhado (blocos, regras, variáveis) fica num lugar só.
 *
 * VERSIONAMENTO APPEND-ONLY, como em `careForms`: editar cria versão nova,
 * `publishedVersion` aponta a versão viva, e cada resposta grava a versão que
 * respondeu. Sem isso, mexer num checkpoint publicado reescreveria o
 * significado de respostas já coletadas — inaceitável em dado clínico.
 *
 * Estes documentos NÃO substituem `careForms`: aquela coleção continua servindo
 * os formulários de adesão (o cadastro do POG e a barreira de programa). São
 * mundos separados de propósito — ver docs/PLANO-builder-cuidado.md.
 */

import { z } from 'zod';
import { grantCoversItem, isGrantActive, type Grant } from './grant.schema';
import { normalizeBrandId, sameBrand, type BrandRef } from './brand';
import { CareBlockSchema } from './care-block.schema';
import { CareActionSchema } from './care-action.schema';
import { CareConditionSchema } from './care-condition.schema';

/** Tipo do item — a "pergunta única" do assistente de criação. */
export const CareItemTypeEnum = z.enum(['CHECKPOINT', 'REMINDER']);
export type CareItemType = z.infer<typeof CareItemTypeEnum>;

/**
 * Jornadas que o builder endereça. Espelha `UserTypeEnum`/`TrailStage` para
 * que a variável nativa `jornada` case sem tradução, mas mantém o vocabulário
 * do épico clínico (PUERPERIO em vez de POSTPARTUM não vale a pena: o resto do
 * repo já usa POSTPARTUM, e divergir criaria uma segunda tradução).
 */
export const CareJourneyEnum = z.enum([
  'TENTANTE',
  'PREGNANT',
  'POSTPARTUM',
  'MAE_DE_ANJO',
  'ADOTANTE',
  'MAE',
]);
export type CareJourney = z.infer<typeof CareJourneyEnum>;

/**
 * Para quem o item vale dentro da linha de cuidado.
 *
 * `FATHER_FIGURE` já foi um valor de `journey`, irmão de PREGNANT e POSTPARTUM.
 * Não funcionava: figura paterna não é uma linha de cuidado, é quem acompanha
 * uma. Como o `currentPregnancyId` dela aponta para a gestação da parceira, a
 * jornada resolvida virava POSTPARTUM no parto e os itens marcados para ela
 * sumiam em silêncio — justo quando o acompanhante mais importa. Separando as
 * duas dimensões, o item declara a linha (puérpera) e a audiência
 * (acompanhante), e o parto deixa de apagá-lo.
 *
 * `AMBOS` existe para o conteúdo que serve aos dois sem reescrita; quando o
 * texto muda de pessoa ("sua recuperação" vs "a recuperação dela"), o certo
 * são dois itens.
 */
export const CareAudienceEnum = z.enum(['TITULAR', 'ACOMPANHANTE', 'AMBOS']);
export type CareAudience = z.infer<typeof CareAudienceEnum>;

/**
 * A jornada vale para esta paciente? (fase 4 do plano "A Eva deixa de ser
 * especial")
 *
 * Antes, item sem marca era "de todo mundo" (`partnerMatchesUser`). Agora
 * item é da marca dona, e chega a outra marca por CONCESSÃO `CARE_JOURNEYS`
 * — inteira ou por item —, menos o que a marca de destino ocultou. A Eva
 * entra na regra como qualquer origem.
 */
export function jornadaVisivel(
  item: { id: string; partnerId?: string | null },
  marcaDaPaciente: BrandRef,
  concessoesRecebidas: ReadonlyArray<Pick<Grant, 'sourceBrandId' | 'kind' | 'scope' | 'status' | 'revokedAt'>>,
  ocultas: ReadonlySet<string> = new Set()
): boolean {
  const dona = normalizeBrandId(item.partnerId ?? null);
  const dela = normalizeBrandId(marcaDaPaciente);
  if (dona === dela) return true;
  if (ocultas.has(item.id)) return false;
  return concessoesRecebidas.some(
    (g) => g.kind === 'CARE_JOURNEYS' && sameBrand(g.sourceBrandId, dona) && isGrantActive(g as Grant) && grantCoversItem(g as Grant, item.id)
  );
}

export const CareItemStatusEnum = z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED']);
export type CareItemStatus = z.infer<typeof CareItemStatusEnum>;

/**
 * Obrigatoriedade. `ENCOURAGED_PENDING` não bloqueia nada — reaparece como
 * pendência até ser concluído, que é o "obrigatório leve" das specs clínicas.
 * `BLOCKING` existe no vocabulário mas NÃO é honrado na fase 1: bloquear acesso
 * é competência da barreira (`careForms`), e juntar os dois exige a decisão de
 * integração que ficou para depois.
 */
export const CarePriorityEnum = z.enum(['OPTIONAL', 'ENCOURAGED_PENDING', 'BLOCKING']);
export type CarePriority = z.infer<typeof CarePriorityEnum>;

/**
 * Quando o item entra na vida da usuária.
 * `CONTEXT` aceita qualquer condição sobre variáveis — inclusive as nativas do
 * prontuário —, que é como "se hipertensa → controle de pressão" se expressa
 * sem código.
 */
export const CareActivationSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('IMMEDIATE') }),
  z.object({
    mode: z.literal('GESTATIONAL_WEEK'),
    startWeek: z.number().int().min(0).max(45),
    endWeek: z.number().int().min(0).max(45),
  }),
  z.object({
    mode: z.literal('BABY_AGE'),
    startDays: z.number().int().min(0),
    endDays: z.number().int().min(0),
  }),
  z.object({ mode: z.literal('CONTEXT'), condition: CareConditionSchema }),
  z.object({ mode: z.literal('AFTER_ITEM'), itemId: z.string() }),
]);
export type CareActivation = z.infer<typeof CareActivationSchema>;


/**
 * Regra de item: combinação de variáveis avaliada AO FINAL do fluxo.
 * Distinta da regra de bloco (que vive na opção e roda na hora da resposta).
 * É o que cobre pontuação agregada e condições compostas — o exemplo canônico
 * da spec é a sugestão de nutricionista com 9 gatilhos em OR.
 */
export const CareItemRuleSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  /**
   * `null` = regra incondicional, sempre aplicada.
   *
   * A especificação clínica pede isso literalmente ("Condição: independente da
   * resposta") para as ações de fechamento — organizar medicações, aula de
   * suplementos. Antes só existia condição obrigatória, e um `null` gravado por
   * fora quebrava o motor em tempo de avaliação em vez de falhar na validação.
   */
  condition: CareConditionSchema.nullable(),
  actions: z.array(CareActionSchema).min(1),
});
export type CareItemRule = z.infer<typeof CareItemRuleSchema>;

// ---------------------------------------------------------------------------
// Configuração específica de LEMBRETE (fase 2)
// ---------------------------------------------------------------------------

/**
 * Notificação agendada por deslocamento relativo à data do registro.
 * Negativo = antes. O padrão observado nas specs é -7d / -1d / -2h, mas o
 * builder aceita qualquer offset.
 */
export const CareReminderNotificationSchema = z.object({
  id: z.string(),
  offsetMinutes: z.number().int(),
  title: z.string(),
  body: z.string(),
  /** Atalhos exibidos na notificação (deep links do app). */
  deepLinks: z.array(z.object({ label: z.string(), route: z.string() })).default([]),
});


/**
 * Regra de acompanhamento: dispara sozinha, sem resposta da usuária.
 * Ex.: "sem próxima consulta agendada → notificar de novo em 7 dias".
 */
export const CareReminderFollowUpSchema = z.object({
  enabled: z.boolean().default(false),
  afterDays: z.number().int().positive().default(7),
  title: z.string().optional(),
  body: z.string().optional(),
});

export const CareReminderConfigSchema = z.object({
  notifications: z.array(CareReminderNotificationSchema).default([]),
  /**
   * Ocupação exigida na rede para o agendamento pelo app fazer sentido.
   *
   * `allowsAppScheduling` diz que o lembrete ACEITA agenda; isto diz de QUEM.
   * Um encaminhamento para psicologia não se resolve com a agenda de qualquer
   * profissional que o parceiro tenha, e oferecer o botão numa rede que não tem
   * psicóloga leva a usuária a uma lista onde ela não encontra o que precisa.
   *
   * Quando a rede do parceiro não cobre a ocupação, a API devolve
   * `allowsAppScheduling: false` — o lembrete continua existindo e continua
   * perguntando se ela agendou, só não promete marcar por aqui.
   */
  schedulingOccupation: z.string().optional(),
  followUp: CareReminderFollowUpSchema.optional(),

  /**
   * Deixa a usuária agendar pelo próprio app, quando o parceiro dela tem agenda
   * configurada. `false` = ela informa a data que já marcou por fora.
   *
   * É um sinal, não um vínculo: o lembrete é global e a agenda é do parceiro,
   * então apontar para uma agenda específica só funcionaria para um deles. O app
   * oferece as agendas ativas daquele parceiro e ela escolhe — o mesmo que
   * `OPEN_SCHEDULING` já fazia com `mappingId` ausente.
   *
   * Sobe de nível sem quebrar: quando existir especialidade ou tipo de consulta,
   * o campo vira opcional e `null` continua significando "ela escolhe".
   */
  allowsAppScheduling: z.boolean().default(false),

  /**
   * Quantas vezes o card pode voltar a perguntar dentro da janela de ativação.
   * `null` = sem teto.
   *
   * Existe para evitar o constrangimento de perguntar "já marcou a próxima?"
   * depois da última consulta do trimestre — a próxima pertence ao lembrete do
   * trimestre seguinte, e a usuária seria questionada sobre algo que ainda não
   * existe para ela. O time clínico já modela o pré-natal como três lembretes,
   * um por trimestre; o teto é quantas consultas cabem em cada um.
   *
   * É um TETO, não um cronograma: não gera data nenhuma, só para de perguntar.
   */
  maxOccurrences: z.number().int().positive().nullable().default(null),
});
export type CareReminderConfig = z.infer<typeof CareReminderConfigSchema>;

// ---------------------------------------------------------------------------
// Versão e item
// ---------------------------------------------------------------------------

/**
 * Soma das pontuações de um checkpoint numa variável (15/10/2026) — o que faz
 * PHQ-9 e EPDS virarem um número que as regras finais leem ("escore ≥ 10 →
 * indicar conteúdo").
 *
 * Mora na versão do checkpoint, não no catálogo de variáveis. Antes a soma
 * era uma "variável calculada" (`CareVariable.calculation`), configurada à
 * parte, presa a UM checkpoint e com os blocos digitados por id: a cópia de
 * um checkpoint não levava a soma, e um id errado não dava aviso nenhum. Em
 * produção, nenhuma das 9 cópias do PHQ-9 e do EPDS somava. As variáveis
 * calculadas antigas continuam valendo; a soma do checkpoint vence quando
 * as duas escrevem a mesma variável.
 */
export const SomaDePontuacaoSchema = z.object({
  /** Variável (do catálogo, numérica) que recebe o total. */
  variavel: z.string().min(1),
  /** Blocos que entram na soma. Vazio = todos os blocos com pontuação. */
  blocos: z.array(z.string()).default([]),
});
export type SomaDePontuacao = z.infer<typeof SomaDePontuacaoSchema>;

export const CareItemVersionSchema = z.object({
  version: z.number().int().positive(),
  blocks: z.array(CareBlockSchema).default([]),
  itemRules: z.array(CareItemRuleSchema).default([]),
  /** Somas de pontuação deste checkpoint. Opcional: versões antigas não têm. */
  somas: z.array(SomaDePontuacaoSchema).default([]),
  reminderConfig: CareReminderConfigSchema.nullable().optional(),
  createdAt: z.date(),
  createdBy: z.string().nullable().optional(),
  /** Nota de versão, opcional — ajuda a equipe clínica a navegar o histórico. */
  note: z.string().nullable().optional(),
  /**
   * Presente quando a versão veio de uma proposta feita por IA (conector
   * MCP). É o que o admin mostra como selo "gerado com IA". Opcional: versões
   * anteriores não têm, e continuam válidas.
   */
  origemIA: z
    .object({ propostaId: z.string(), cliente: z.string().nullable(), aprovadaPor: z.string().nullable() })
    .nullable()
    .optional(),
});
export type CareItemVersion = z.infer<typeof CareItemVersionSchema>;

/**
 * Converte o formato anterior, em que `journey: 'FATHER_FIGURE'` acumulava
 * linha e audiência. Nunca houve deploy — isso alcança apenas documentos
 * semeados em emulador local. Some quando não houver mais nenhum.
 */
const migrarFatherFigure = (raw: unknown) => {
  if (!raw || typeof raw !== 'object') return raw;
  const doc = raw as Record<string, unknown>;
  if (doc.journey !== 'FATHER_FIGURE') return raw;
  return { ...doc, journey: 'PREGNANT', audience: 'ACOMPANHANTE' };
};

export const CareItemSchema = z.preprocess(
  migrarFatherFigure,
  z.object({
    id: z.string(),
    type: CareItemTypeEnum,

    /** Nome interno (catálogo do admin) e título exibido à usuária. */
    internalName: z.string(),
    title: z.string(),
    description: z.string().nullable().optional(),

    journey: CareJourneyEnum,

    /**
     * Default TITULAR: item sem intenção declarada não vaza para o acompanhante.
     */
    audience: CareAudienceEnum.default('TITULAR'),

    /**
     * `null` = item global da Eva. A fase 1 só cria globais; itens por parceiro
     * dependem da decisão de herança (aditiva, como o Page Builder — e não o
     * filtro estrito do careForms). Ver plano, §11.
     */
    partnerId: z.string().nullable().default(null),

    status: CareItemStatusEnum.default('DRAFT'),
    activation: CareActivationSchema,
      priority: CarePriorityEnum.default('OPTIONAL'),

    /**
     * Se a usuária pode refazer o item do zero depois de concluído. Não é edição
     * de resposta pontual — é nova rodada completa; a variável fica com o valor
     * mais recente e o histórico guarda as anteriores.
     */
    allowsNewResponse: z.boolean().default(false),

    /** Tempo estimado em minutos, exibido no card ("3 min"). */
    estimatedMinutes: z.number().int().positive().nullable().optional(),

    publishedVersion: z.number().int().positive().nullable().default(null),
    versions: z.array(CareItemVersionSchema).default([]),

      createdAt: z.date(),
      updatedAt: z.date(),
      deletedAt: z.date().nullable().default(null),
  })
);

export type CareItem = z.infer<typeof CareItemSchema>;

// ---------------------------------------------------------------------------
// Helpers compartilhados entre API, admin e app
// ---------------------------------------------------------------------------

/** Versão publicada de um item, ou `null` se só houver rascunho. */
export function getPublishedVersion(item: CareItem): CareItemVersion | null {
  if (item.publishedVersion == null) return null;
  return item.versions.find((v) => v.version === item.publishedVersion) ?? null;
}

/**
 * Se um item de determinada audiência vale para quem tem esse `papel`.
 *
 * `papel` desconhecido (cadastro incompleto) conta como titular: é o caminho
 * conservador — a acompanhante não recebe o texto escrito em primeira pessoa.
 */
export function audienceMatchesRole(audience: CareAudience, papel: unknown): boolean {
  if (audience === 'AMBOS') return true;
  const acompanhante = papel === 'ACOMPANHANTE';
  return audience === 'ACOMPANHANTE' ? acompanhante : !acompanhante;
}

/** Última versão gravada (a que o editor abre). */
export function getLatestVersion(item: CareItem): CareItemVersion | null {
  if (!item.versions.length) return null;
  return item.versions.reduce((max, v) => (v.version > max.version ? v : max), item.versions[0]);
}

/**
 * O selo "gerado com IA": a origem da versão mais recente, enquanto ela for
 * rascunho. Publicou, o selo sai — decisão de 25/09/2026: a partir dali a
 * jornada é da equipe que revisou e publicou. O `origemIA` continua gravado
 * na versão, como rastro; só deixa de ser mostrado.
 */
export function origemIAEmRascunho(item: Pick<CareItem, 'versions' | 'publishedVersion'>): NonNullable<CareItemVersion['origemIA']> | null {
  const ultima = getLatestVersion(item as CareItem);
  if (!ultima?.origemIA) return null;
  if (item.publishedVersion != null && ultima.version <= item.publishedVersion) return null;
  return ultima.origemIA;
}
